import React, { FC, useMemo, useCallback } from 'react'
import { useLocalStorage } from 'react-use'

import { localStorageKeys } from '@sketch/constants'
import { routes } from '@sketch/modules-common'

import { ReactComponent as ProfileCard } from '@sketch/icons/profile-card-4-fill-wide'

import { ProfileModal } from '../../modals'
import { PopoverInfo } from './PopoverInfo'

import { useGetWorkspaceProfile, useIsProfileCreated } from '../../hooks'
import {
  useModalContext,
  SelectDropdownLinkItem,
  SelectDropdownButtonItem,
} from '@sketch/components'

import { WorkspaceMinimalFragment } from '@sketch/gql-types'
import { StyledPill } from './PublicProfileSidebarItem.style'

interface PublicProfileSidebarItemProps {
  workspace: WorkspaceMinimalFragment
  isAdmin: boolean
}

interface CreateLinkToProfileProps {
  isAdmin: boolean
  workspaceId: string
  shortUrlName: string
}

const createLinkToProfile = ({
  shortUrlName,
  workspaceId,
  isAdmin,
}: CreateLinkToProfileProps): string => {
  if (isAdmin) {
    return routes.WORKSPACE_PROFILE_EDIT.create({ workspaceId })
  }

  return routes.WORKSPACE_PROFILE.create({ shortUrlName })
}

export const PublicProfileSidebarItem: FC<PublicProfileSidebarItemProps> = ({
  workspace,
  isAdmin,
}) => {
  const { isProfileCreated } = useIsProfileCreated(workspace.identifier)

  const { showModal } = useModalContext()

  const { profile } = useGetWorkspaceProfile({
    variables: {
      workspaceIdentifier: workspace.identifier,
    },
    skip: !isProfileCreated,
  })

  const [publicProfilePopoverSeen, setPublicProfilePopoverSeen] =
    useLocalStorage(localStorageKeys.publicProfilePopoverSeen, false)

  const linkToProfile = useMemo(() => {
    if (!isProfileCreated || !profile?.shortUrlName) return ''

    return createLinkToProfile({
      workspaceId: workspace.identifier,
      shortUrlName: profile.shortUrlName,
      isAdmin,
    })
  }, [isProfileCreated, workspace.identifier, profile?.shortUrlName, isAdmin])

  const isGuest = workspace.userRole === 'GUEST'
  const isViewerNotAdmin = !workspace.userCanEdit && !isAdmin
  const isEditorNotAdmin = workspace.userCanEdit && !isAdmin
  const isTooltipVisible = !isGuest && !isViewerNotAdmin

  const showPublicProfileModal = useCallback(() => {
    if (!workspace || isEditorNotAdmin) {
      return
    }

    showModal(ProfileModal, {
      workspace,
    })
  }, [workspace, isEditorNotAdmin, showModal])

  const handleLinkClick = useCallback(() => {
    setPublicProfilePopoverSeen(true)
  }, [setPublicProfilePopoverSeen])

  const popoverAction = useMemo(
    () => (
      <PopoverInfo
        isProfileCreated={isProfileCreated}
        isAdmin={isAdmin}
        show={isTooltipVisible}
        onConfirm={showPublicProfileModal}
      />
    ),
    [isProfileCreated, isAdmin, isTooltipVisible, showPublicProfileModal]
  )

  const target = isProfileCreated && !isAdmin ? '_blank' : undefined

  const isActive =
    window.location.pathname ===
    routes.WORKSPACE_PROFILE_EDIT.create({ workspaceId: workspace.identifier })

  const text = (
    <>Public Profile {!isAdmin && isProfileCreated && <span>↗</span>}</>
  )

  const iconConfig = {
    icon: ProfileCard,
    highlight: !publicProfilePopoverSeen,
  }

  if (isProfileCreated) {
    return (
      <SelectDropdownLinkItem
        icon={iconConfig}
        text={text}
        to={linkToProfile}
        target={target}
        showActionsOnHover
        onClick={handleLinkClick}
        isActive={() => isActive}
        action={popoverAction}
      />
    )
  }

  if (isViewerNotAdmin || isEditorNotAdmin || isGuest) {
    return null
  }

  return (
    <SelectDropdownButtonItem
      icon={iconConfig}
      text={text}
      showActionsOnHover
      onClick={showPublicProfileModal}
      action={popoverAction}
      additional={
        !publicProfilePopoverSeen && (
          <StyledPill variant="secondary">New</StyledPill>
        )
      }
    />
  )
}
